import {
  customElement,
  type TemplateResult,
  property,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-radiogroup.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-radiogroup': DeesInputRadiogroup;
  }
}

type RadioOption = string | { option: string; key: string; payload?: any };

@customElement('dees-input-radiogroup')
export class DeesInputRadiogroup extends DeesInputBase<string | object> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property({ type: Array })
  accessor options: RadioOption[] = [];

  @property()
  accessor selectedOption: string = '';

  @property({ type: String })
  accessor direction: 'vertical' | 'horizontal' = 'vertical';

  @property({ type: String, reflect: true })
  accessor validationState: 'valid' | 'invalid' | 'warn' | 'pending' | null = null;

  // Form compatibility
  public get value() {
    const option = this.getOptionByKey(this.selectedOption);
    if (typeof option === 'object' && option.payload !== undefined) {
      return option.payload;
    }
    return this.selectedOption;
  }

  public set value(val: string | any) {
    if (typeof val === 'string') {
      this.selectedOption = val;
    } else {
      // Try to find option by payload
      const option = this.options.find(opt => 
        typeof opt === 'object' && opt.payload === val
      );
      if (option && typeof option === 'object') {
        this.selectedOption = option.key;
      }
    }
  }

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      * {
        box-sizing: border-box;
      }

      :host {
        display: block;
        position: relative;
        font-family: var(--dees-font-family);
      }

      .maincontainer {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-sm);
      }

      .maincontainer.horizontal {
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--dees-spacing-lg);
      }

      .radio-option {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) 0;
        cursor: pointer;
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        user-select: none;
        position: relative;
        border-radius: var(--dees-radius-sm);
      }

      .maincontainer.horizontal .radio-option {
        padding: var(--dees-spacing-xs) var(--dees-spacing-lg) var(--dees-spacing-xs) 0;
      }

      .radio-option:hover .radio-circle {
        border-color: color-mix(in srgb, var(--dees-color-text-primary) 35%, transparent);
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      .radio-option:hover .radio-label {
        color: var(--dees-color-text-primary);
      }

      .radio-circle {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid color-mix(in srgb, var(--dees-color-text-primary) 35%, transparent);
        background: var(--dees-color-bg-primary);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        position: relative;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .radio-option.selected .radio-circle {
        border-color: var(--dees-color-accent-primary);
        background: var(--dees-color-accent-primary);
      }

      .radio-option.selected .radio-circle::after {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--dees-color-bg-primary);
        transform: scale(0);
        transition: transform var(--dees-transition-default) var(--dees-ease-standard);
      }

      .radio-option.selected .radio-circle::after {
        transform: scale(1);
      }

      .radio-circle:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--dees-color-bg-primary),
                    0 0 0 4px var(--dees-color-accent-primary);
      }

      .radio-label {
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        color: var(--dees-color-text-secondary);
        transition: color var(--dees-transition-default) var(--dees-ease-standard);
        letter-spacing: -0.006em;
        line-height: var(--dees-font-subhead-line);
      }

      .radio-option.selected .radio-label {
        color: var(--dees-color-text-primary);
      }

      :host([disabled]) .radio-option {
        cursor: not-allowed;
        opacity: 0.5;
      }

      :host([disabled]) .radio-option:hover .radio-circle {
        border-color: color-mix(in srgb, var(--dees-color-text-primary) 35%, transparent);
        background: var(--dees-color-bg-primary);
      }

      :host([disabled]) .radio-option:hover .radio-label {
        color: var(--dees-color-text-secondary);
      }

      .label-text {
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        color: var(--dees-color-text-primary);
        margin-bottom: var(--dees-spacing-sm);
        letter-spacing: -0.006em;
        line-height: var(--dees-font-subhead-line);
      }

      /* Validation styles */
      :host([validationState="invalid"]) .radio-circle {
        border-color: var(--dees-color-accent-error);
      }

      :host([validationState="invalid"]) .radio-option.selected .radio-circle {
        border-color: var(--dees-color-accent-error);
        background: var(--dees-color-accent-error);
      }

      :host([validationState="valid"]) .radio-option.selected .radio-circle {
        border-color: var(--dees-color-text-success);
        background: var(--dees-color-text-success);
      }

      :host([validationState="warn"]) .radio-option.selected .radio-circle {
        border-color: var(--dees-color-accent-warning);
        background: var(--dees-color-accent-warning);
      }

      /* Override base grid layout for radiogroup to prevent large gaps */
      :host([label-position="left"]) .input-wrapper {
        grid-template-columns: auto auto;
      }

      :host([label-position="right"]) .input-wrapper {
        grid-template-columns: auto auto;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        ${this.label ? html`<div class="label-text">${this.label}</div>` : ''}
        <div class="maincontainer ${this.direction}">
          ${this.options.map((option) => {
            const optionKey = this.getOptionKey(option);
            const optionLabel = this.getOptionLabel(option);
            const isSelected = this.selectedOption === optionKey;
            
            return html`
              <div 
                class="radio-option ${isSelected ? 'selected' : ''}"
                @click="${() => this.selectOption(optionKey)}"
                @keydown="${(e: KeyboardEvent) => this.handleKeydown(e, optionKey)}"
              >
                <div 
                  class="radio-circle" 
                  tabindex="${this.disabled ? '-1' : '0'}"
                  role="radio"
                  aria-checked="${isSelected}"
                  aria-label="${optionLabel}"
                ></div>
                <div class="radio-label">${optionLabel}</div>
              </div>
            `;
          })}
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  private getOptionKey(option: RadioOption): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.key;
  }

  private getOptionLabel(option: RadioOption): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.option;
  }

  private getOptionByKey(key: string): RadioOption | undefined {
    return this.options.find(opt => this.getOptionKey(opt) === key);
  }

  private selectOption(key: string): void {
    if (this.disabled) {
      return;
    }

    const oldValue = this.selectedOption;
    this.selectedOption = key;

    if (oldValue !== key) {
      this.dispatchEvent(new CustomEvent('change', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      }));
      
      this.dispatchEvent(new CustomEvent('input', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      }));
      
      this.changeSubject.next(this);
    }
  }

  public getValue(): string | any {
    return this.value;
  }

  public setValue(val: string | any): void {
    this.value = val;
  }

  public async validate(): Promise<boolean> {
    if (this.required && !this.selectedOption) {
      this.validationState = 'invalid';
      return false;
    }
    
    this.validationState = 'valid';
    return true;
  }

  public async firstUpdated() {
    // Auto-select first option if none selected and not required
    if (!this.selectedOption && this.options.length > 0 && !this.required) {
      const firstOption = this.options[0];
      this.selectedOption = this.getOptionKey(firstOption);
    }
  }

  private handleKeydown(event: KeyboardEvent, optionKey: string) {
    if (this.disabled) return;
    
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.selectOption(optionKey);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusNextOption();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusPreviousOption();
    }
  }

  private focusNextOption() {
    const radioCircles = Array.from(this.shadowRoot!.querySelectorAll('.radio-circle'));
    const currentIndex = radioCircles.findIndex(el => el === this.shadowRoot!.activeElement);
    const nextIndex = (currentIndex + 1) % radioCircles.length;
    (radioCircles[nextIndex] as HTMLElement).focus();
  }

  private focusPreviousOption() {
    const radioCircles = Array.from(this.shadowRoot!.querySelectorAll('.radio-circle'));
    const currentIndex = radioCircles.findIndex(el => el === this.shadowRoot!.activeElement);
    const prevIndex = currentIndex <= 0 ? radioCircles.length - 1 : currentIndex - 1;
    (radioCircles[prevIndex] as HTMLElement).focus();
  }
}